import { Injectable, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { map } from 'rxjs/operators';

import {
  IBaseGridState,
  IGridWithState,
  IGridStateManager,
  IGridStateManagerConfig,
} from './grid-state-manager.interface';

/**
 * Universal service for grid state management
 */
@Injectable()
export class GridStateManagerService<TState extends IBaseGridState> implements IGridStateManager<TState>, OnDestroy {
  private config: IGridStateManagerConfig<TState>;
  private subscription: Subscription;

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
  ) {}

  /**
   * Initializes state management
   */
  initialize(config: IGridStateManagerConfig<TState>): void {
    this.config = config;
    this.loadState();
  }

  /**
   * Loads state from URL or sessionStorage
   */
  loadState(): void {
    if (!this.config) {
      console.warn('GridStateManager not initialized');
      return;
    }

    this.subscription = this.activatedRoute.queryParams
      .pipe(
        map((params) => {
          if (params.state) {
            return this.parseStateFromUrl(params.state);
          }
          return this.loadStateFromStorage();
        }),
      )
      .subscribe((state) => {
        if (state) {
          this.applyState(state);
        }
      });
  }

  /**
   * Saves the current state
   */
  saveState(): void {
    if (!this.config) {
      console.warn('GridStateManager not initialized');
      return;
    }

    const currentState = this.getCurrentState();
    const stateString = JSON.stringify(currentState);

    // Save to sessionStorage
    const storageKey = this.getStorageKey();
    if (storageKey) {
      sessionStorage.setItem(storageKey, stateString);
    }
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { state: btoa(stateString) },
      replaceUrl: true,
    });
  }

  /**
   * Applies state to component
   */
  applyState(state: TState): void {
    if (!this.config) {
      console.warn('GridStateManager not initialized');
      return;
    }

    if (this.config.applyState) {
      this.config.applyState(state);
    }
  }

  /**
   * Resets state to default values
   */
  resetState(): void {
    if (!this.config) {
      console.warn('GridStateManager not initialized');
      return;
    }

    // Remove state from sessionStorage
    const storageKey = this.getStorageKey();
    if (storageKey) {
      sessionStorage.removeItem(storageKey);
    }

    // Clear URL
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: {},
      replaceUrl: true,
    });
  }

  /**
   * Gets the current state
   */
  getCurrentState(): TState {
    if (!this.config) {
      console.warn('GridStateManager not initialized');
      return null;
    }

    if (this.config.getCurrentState) {
      return this.config.getCurrentState();
    }

    return null;
  }

  /**
   * Destroys the service and unsubscribes from subscriptions
   */
  destroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = null;
    }
  }

  /**
   * Implementation of OnDestroy interface
   */
  ngOnDestroy(): void {
    this.destroy();
  }

  /**
   * Loads state from sessionStorage
   */
  private loadStateFromStorage(): TState | null {
    const storageKey = this.getStorageKey();
    if (!storageKey) {
      return null;
    }

    return this.parseStateFromStorage(storageKey);
  }

  /**
   * Parses state from URL
   */
  private parseStateFromUrl(encodedState: string): TState | null {
    try {
      const decoded = JSON.parse(atob(encodedState));
      return this.normalizeState(decoded);
    } catch (e) {
      console.error('Error parsing grid state from query params:', e);
      return null;
    }
  }

  /**
   * Parses state from sessionStorage
   */
  private parseStateFromStorage(storageKey: string): TState | null {
    try {
      const savedState = sessionStorage.getItem(storageKey);
      if (!savedState) {
        return null;
      }

      const parsed = JSON.parse(savedState);
      return this.normalizeState(parsed);
    } catch (e) {
      sessionStorage.removeItem(storageKey);
      console.error('Error parsing grid state from sessionStorage:', e);
      return null;
    }
  }

  /**
   * Normalizes state
   */
  private normalizeState(state: any): TState | null {
    if (!this.config.normalizeState) {
      return state;
    }

    return this.config.normalizeState(state);
  }

  /**
   * Gets the key for storing state
   */
  private getStorageKey(): string | null {
    if (!this.config) {
      return null;
    }

    return this.config.storageKey;
  }
}

/**
 * Factory for creating state normalizers
 */
export class GridStateNormalizers {
  /**
   * Creates a normalizer for state with Kendo state and filter
   */
  static createGridWithFilterNormalizer<TFilter extends { Start?: Date; End?: Date }>(
    defaultFilter: TFilter,
  ): (state: any) => IGridWithState<TFilter> | null {
    return (state: any) => {
      if (!state?.filter) {
        return state;
      }

      return {
        ...state,
        filter: {
          ...state.filter,
          Start: state.filter.Start ? new Date(state.filter.Start) : defaultFilter.Start,
          End: state.filter.End ? new Date(state.filter.End) : defaultFilter.End,
        } as TFilter,
      };
    };
  }
}
